import type {
  WebhookDeliveryAttempt as GQLItem,
  WebhookDeliveryAttemptConnection as GQLConnection,
} from '../../../generated/api.graphql';
import { take } from '../../../util';
import * as GqlConnection from '../../connection/GqlConnection';
import * as PageInfo from '../../connection/PageInfo';

/** One attempt to deliver a webhook to a subscription; the backend keeps no payload for it. */
export type WebhookDeliveryAttempt = {
  id: number;
  attempt: number;
  attemptedAt: string;
  durationMs: number;
  topic: string;
  recordId: number;
  responseStatus: number | null;
  failureKind: string | null;
};

export type FailureKind = 'timeout' | 'connection_failed' | 'delivery_error';

export const fromGql = (item: GQLItem): WebhookDeliveryAttempt => ({
  ...take<WebhookDeliveryAttempt>(item, 'id', 'attempt', 'attemptedAt', 'durationMs', 'topic', 'recordId'),
  responseStatus: item.responseStatus ?? null,
  failureKind: item.failureKind ?? null,
});

/** `edges` and `node` are nullable on the schema, so the shared `GqlConnection.fromGql` does not fit. */
export const fromGqlConnection = (connection: GQLConnection): GqlConnection.GqlConnection<WebhookDeliveryAttempt> => {
  const edges = (connection.edges ?? [])
    .filter(edge => Boolean(edge?.node))
    .map(edge => ({ cursor: edge!.cursor, node: fromGql(edge!.node as GQLItem) }));

  return {
    edges,
    nodes: edges.map(edge => edge.node),
    pageInfo: PageInfo.fromGql(connection.pageInfo),
  };
};

/** Whether the subscriber accepted the attempt with a 2xx response. */
export const delivered = ({ responseStatus }: WebhookDeliveryAttempt): boolean =>
  responseStatus !== null && responseStatus >= 200 && responseStatus < 300;
